/**
 * Department/Agency name normalization utilities
 *
 * Federal data often contains inverted department names like "JUSTICE, DEPARTMENT OF"
 * which should be displayed as "DEPARTMENT OF JUSTICE" for better readability.
 * Original case is preserved.
 */

/**
 * Pattern to detect inverted department names
 * Matches: "NAME, DEPARTMENT OF" or "NAME, DEPT OF" or "NAME, DEPT. OF"
 * Case insensitive for matching, but preserves original case
 */
const INVERTED_DEPARTMENT_PATTERN = /^(.+?),\s*(DEPARTMENT|DEPT\.?)\s+(OF)$/i

/**
 * Normalize a department/agency name to proper format
 *
 * Handles inverted names like "JUSTICE, DEPARTMENT OF" → "DEPARTMENT OF JUSTICE"
 * Preserves the original case.
 *
 * @param name - The department or agency name to normalize
 * @returns The normalized name, or the original if no transformation needed
 *
 * @example
 * normalizeDepartmentName('JUSTICE, DEPARTMENT OF') // 'DEPARTMENT OF JUSTICE'
 * normalizeDepartmentName('DEFENSE, DEPARTMENT OF') // 'DEPARTMENT OF DEFENSE'
 * normalizeDepartmentName('Health and Human Services, Department of') // 'Department of Health and Human Services'
 * normalizeDepartmentName('Treasury, Dept. of') // 'Dept. of Treasury'
 * normalizeDepartmentName('Department of Justice') // 'Department of Justice' (unchanged)
 * normalizeDepartmentName('NASA') // 'NASA' (unchanged)
 */
export function normalizeDepartmentName(
  name: string | null | undefined,
): string | null {
  if (!name) return null

  const trimmedName = name.trim()
  if (!trimmedName) return null

  // Check for inverted pattern
  const match = trimmedName.match(INVERTED_DEPARTMENT_PATTERN)
  if (match) {
    const departmentName = match[1].trim()
    const deptWord = match[2] // "DEPARTMENT" or "DEPT" or "DEPT."
    const ofWord = match[3] // "OF"
    // Reorder to "Department of Name", preserving original case
    return `${deptWord} ${ofWord} ${departmentName}`
  }

  return trimmedName
}
